<script setup lang="ts">
// Vue
import { ref, withDefaults, computed } from "vue";

// Data Providers
import { TransportTypeOptionType, useTransportTypeOptions } from "@/dataProviders/TransportTypeOptions";
import { TransportTypesType } from "@/features/BuildingProject";
import { useIndicators, IndicatorTypeType } from "@/dataProviders/Indicators";

// Components
import { ExternalLink, Lock, LockOpen, Trash2 } from "@lucide/vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import InputLabel from "@/components/Reusables/Inputs/InputLabel.vue";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";

// Utils
import format from "@/services/Utils/format";

// GraphQL
import { DenominatorUnit, Transport_Type_Enum } from "@/graphql";

const props = withDefaults(
  defineProps<{
    transportLabel?: string;
    transportTypeOptions: TransportTypeOptionType[];
    type?: "default" | "generic";
    item: TransportTypesType;
    wasValidated: boolean;
    allowDelete?: boolean;
    /**
     * The row belongs to a revision being previewed, so it is history: never editable, and the
     * Default row's unlock button is meaningless. The parent's `<fieldset disabled>` already makes
     * the controls inert; this is what makes them LOOK it, since each input greys itself from its
     * own `disabled` prop (RTLCA-5772).
     */
    readOnly?: boolean;
  }>(),
  {
    transportLabel: "Type",
    type: "generic",
    allowDelete: false,
    readOnly: false,
  }
);

const transportTypeOptionsProvider = useTransportTypeOptions();
const indicators = useIndicators();

// The Default row starts locked and can be unlocked by its own button; a previewed row can never be.
const disabled = ref<boolean>(props.type === "default");
const isDisabled = computed(() => props.readOnly || disabled.value);

const emits = defineEmits(["handleRemoveTransport"]);

const handleToggleEditDefaultTransport = () => {
  if (props.readOnly) return;
  disabled.value = !disabled.value;
};

const handleSelectOption = (event: any) => {
  // The row is written in place below, and a previewed row is the revisions store's own object.
  if (props.readOnly) return;
  if (!event.currentTarget) return;
  if (!props.transportTypeOptions) return;

  // Find option
  const option = props.transportTypeOptions.find((type) => type.id === event.currentTarget.value);
  if (!option) return;

  // Set values for transport type
  props.item.transport.id = option.id;
  props.item.transport.transportType = option.transportType;
  props.item.emission = option.emission;
  props.item.transport.emission = option.emission;
  props.item.distance = option.distance;
  props.item.transport.label = option?.label || "";
  props.item.transport.indicatorType.id = option.indicatorType.id;
};

const isCustom = computed(() => {
  return props.item.transport.transportType === Transport_Type_Enum.Custom;
});

const currentTransportType = computed(() => {
  return transportTypeOptionsProvider.getTransportOptionFromId(props.item.transport.id);
});

const distanceUnit = computed(() => {
  return currentTransportType.value?.distance_unit || "";
});

const emissionUnit = computed(() => {
  if (!currentTransportType.value) return "";
  return indicators.getIndicatorUnit(
    currentTransportType.value.indicatorType as IndicatorTypeType,
    currentTransportType.value.denominatorUnit as DenominatorUnit
  );
});

const totalEmissionUnit = computed(() => {
  if (!currentTransportType.value) return "";
  return indicators.getIndicatorUnit(
    currentTransportType.value.totalIndicatorType as IndicatorTypeType,
    currentTransportType.value.totalDenominatorUnit as DenominatorUnit
  );
});

const totalEmission = computed(() => {
  if (isCustom.value) {
    return format.decimalNumber(props.item.emission * props.item.distance);
  } else {
    return format.decimalNumber(props.item.transport.emission * props.item.distance);
  }
});
</script>

<template>
  <!-- Every field in the row reserves its error message's space (`reserveErrorSpace`), and the buttons
       pad by the same 20px at md and up, where they share the row. The number columns bottom-align
       (`self-end`) to a row whose height the Name field's column sets, so without the reservation a
       message appearing under any one field moved every other field's control relative to it — the
       "Enter a name" case in RTLCA-5771. Reserved, the row is 20px taller than it was and nothing in it
       ever moves again. -->
  <div class="rounded-16 bg-slate-0 grid w-full grid-cols-12 items-start justify-between gap-4 border border-slate-200 p-4">
    <!-- Select is only for default or generic -->
    <div class="col-span-12 flex w-full flex-col space-y-3 md:col-span-5">
      <div
        v-if="type === 'default' || type === 'generic'"
        class="relative flex w-full flex-col">
        <div class="flex items-center gap-1">
          <InputLabel
            id="select"
            :label="transportLabel"
            :required="true" />
          <a
            v-if="transportTypeOptionsProvider.getTransportTypeDataSource(item.transport.id)"
            title="Transportation type emission value data source"
            aria-label="Open the transport emission data source"
            class="focus-ring rounded-4 text-type-muted hover:text-app-blue-600 duration-fast ease-standard flex transition-colors"
            :href="transportTypeOptionsProvider.getTransportTypeDataSource(item.transport.id)"
            target="_blank"
            rel="noopener noreferrer">
            <ExternalLink
              class="icon-sm"
              :stroke-width="1.5" />
          </a>
        </div>
        <select
          id="select"
          class="focus:border-app-blue-600 rounded-8 t-small h-9 w-full border p-2 leading-none outline-hidden"
          data-cy="transport-type"
          :disabled="isDisabled"
          :class="[
            isDisabled ? 'text-type-muted cursor-not-allowed bg-slate-200' : 'bg-slate-0',
            wasValidated && item.transport.transportType === null ? 'border-app-red-500' : 'border-slate-500',
          ]"
          :value="item.transport.id"
          @change="handleSelectOption">
          <option
            value=""
            disabled
            selected>
            Select
          </option>
          <option
            v-for="transportType in transportTypeOptions"
            :key="transportType.id"
            :value="transportType.id">
            {{ transportType.label }}
          </option>
        </select>
      </div>

      <TextInputNew
        v-model="item.transport_type_name as string"
        label="Name"
        placeholder="Enter name"
        :disabled="isDisabled"
        :error-msg="wasValidated && !item.transport_type_name ? 'Enter a name' : ''"
        :reserve-error-space="true"
        :required="true" />
    </div>

    <NumberInput
      :key="item.transport.id"
      v-model:value="item.distance"
      class="col-span-4 self-end sm:col-span-4 md:col-span-2"
      color="primary"
      label="Distance"
      :tooltip="distanceUnit ? `Default distance in ${distanceUnit}. Can be changed during mapping.` : ''"
      :positive="true"
      :align-right="true"
      :error-msg="wasValidated && (item.distance === null || item.distance < 0) ? 'Enter the distance' : ''"
      :reserve-error-space="true"
      :disabled="isDisabled"
      :required="true" />

    <NumberInput
      v-if="isCustom"
      :key="item.transport.id"
      v-model:value="item.emission"
      class="col-span-4 self-end sm:col-span-4 md:col-span-2"
      color="primary"
      label="Emission"
      :tooltip="emissionUnit"
      :positive="true"
      :align-right="true"
      :error-msg="wasValidated && (item.emission === null || item.emission < 0) ? 'Enter the emission' : ''"
      :reserve-error-space="true"
      :disabled="isDisabled || !isCustom"
      :required="true" />

    <NumberInput
      v-if="!isCustom"
      :key="item.transport.id"
      v-model:value="item.transport.emission"
      class="col-span-4 self-end sm:col-span-4 md:col-span-2"
      color="primary"
      label="Emission"
      :tooltip="emissionUnit"
      :positive="true"
      :align-right="true"
      :error-msg="''"
      :reserve-error-space="true"
      :disabled="true"
      :required="true" />

    <NumberInput
      :key="totalEmission"
      class="col-span-4 self-end sm:col-span-4 md:col-span-2"
      :value="totalEmission"
      color="primary"
      label="Total emission"
      :tooltip="totalEmissionUnit"
      :positive="true"
      :align-right="true"
      :reserve-error-space="true"
      :disabled="true" />

    <div class="col-span-12 flex justify-end self-end md:col-span-1 md:pb-5">
      <!-- Default row -->
      <button
        v-if="type === 'default' && !readOnly"
        type="button"
        :aria-label="isDisabled ? 'Unlock default transport for editing' : 'Lock default transport'"
        data-cy="transport-default-edit"
        class="focus-ring interactive-active duration-fast ease-standard rounded-4 p-1 transition-colors hover:bg-slate-100"
        @click="handleToggleEditDefaultTransport">
        <component
          :is="isDisabled ? Lock : LockOpen"
          class="icon-sm"
          :class="isDisabled ? 'text-type-muted' : 'text-app-blue-600'"
          :stroke-width="1.5" />
      </button>

      <!-- Generic row -->
      <button
        v-if="type === 'generic' && allowDelete"
        type="button"
        aria-label="Remove transport type"
        data-cy="transport-remove"
        class="focus-ring interactive-active duration-fast ease-standard rounded-4 p-1 transition-colors hover:bg-slate-100"
        @click="() => emits('handleRemoveTransport', item.id)">
        <Trash2
          class="icon-sm text-app-red-600"
          :stroke-width="1.5" />
      </button>
    </div>
  </div>
</template>
